import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import routeApi from "@/api/route";
import orderApi from "@/api/order";
import deliveryAttemptsApi from "@/api/deliveryAttempts";
import { toError } from "@/shared/utils/toError";
import { deliveryActions } from "./deliverySlice";
import { ERPOrdersQueryParams, RoutesQueryParams } from "@/shared/interfaces";
import { IRouterConfiguration } from "@/shared/interfaces/setting";

function* handleGetRoutes(action: PayloadAction<RoutesQueryParams>) {
  const queryParams = action.payload;
  try {
    const { data } = yield call(routeApi.getRoutes, queryParams);
    yield put(deliveryActions.getRoutesSuccess(data));
  } catch (error) {
    const err = toError(error);
    yield put(deliveryActions.requestFailed());
    toast.error(err.message);
  }
}

function* handleGetRouteDetail(action: PayloadAction<number>) {
  const id = action.payload;
  try {
    const { data } = yield call(routeApi.getRouteSummary, id);
    yield put(deliveryActions.getRouteSuccess(data));
  } catch (error) {
    const err = toError(error);
    yield put(deliveryActions.requestFailed());
    toast.error(err.message);
  }
}

function* handleRefreshRouteDetail(action: PayloadAction<number>) {
  const id = action.payload;
  try {
    const { data } = yield call(routeApi.refreshRoute, id);
    yield put(deliveryActions.getRouteSuccess(data));
  } catch (error) {
    const err = toError(error);
    yield put(deliveryActions.requestFailed());
    toast.error(err.message);
  }
}

function* handleDisconnectRoute(action: PayloadAction<{ deliveryId: number; routeId: number }>) {
  const { deliveryId, routeId } = action.payload;
  try {
    yield put(deliveryActions.markProcessingRoute(routeId));
    yield call(deliveryAttemptsApi.disconnectRoute, deliveryId);
    const { data } = yield call(routeApi.getRouteSummary, routeId);
    yield put(deliveryActions.disconnectRouteSuccess(data));
    yield call(routeApi.recalculateRoute, routeId);
    yield put(deliveryActions.unmarkProcessingRoute(routeId));
    yield put(deliveryActions.getRoutes({ open: true, includeDispatched: false, page: 1, limit: 3000 }));
    toast.success("Disconnected");
  } catch (error) {
    const err = toError(error);
    yield put(deliveryActions.requestFailed());
    yield put(deliveryActions.unmarkProcessingRoute(routeId));
    toast.error(err.message);
  }
}

function* handleRecalculateRoute(action: PayloadAction<{ routeId: number }>) {
  const { routeId } = action.payload;
  try {
    yield put(deliveryActions.markProcessingRoute(routeId));
    yield call(routeApi.recalculateRoute, routeId);
    const { data } = yield call(routeApi.getRouteSummary, routeId);
    yield put(deliveryActions.recalculateRouteSuccess(data));
    yield put(deliveryActions.unmarkProcessingRoute(routeId));
  } catch (error) {
    const err = toError(error);
    yield put(deliveryActions.requestFailed());
    yield put(deliveryActions.unmarkProcessingRoute(routeId));
    toast.error(err.message);
  }
}

function* handleGetNeedActionDelivery() {
  try {
    const { data } = yield call(deliveryAttemptsApi.getDeliveryNeedAction);
    yield put(deliveryActions.getDeliveryAttemptsSuccess(data));
  } catch (error) {
    const err = toError(error);
    yield put(deliveryActions.requestFailed());
    toast.error(err.message);
  }
}

function* handleGetOrders(action: PayloadAction<ERPOrdersQueryParams>) {
  const queryParams = action.payload;
  try {
    const { data } = yield call(orderApi.getOrders, queryParams);
    yield put(deliveryActions.getOrdersSuccess(data));
  } catch (error) {
    const err = toError(error);
    yield put(deliveryActions.requestFailed());
    toast.error(err.message);
  }
}

function* handleGetOrdersToPrepend(action: PayloadAction<ERPOrdersQueryParams>) {
  const queryParams = action.payload;
  try {
    const { data } = yield call(orderApi.getOrders, queryParams);
    yield put(deliveryActions.getOrdersToAppendSuccess(data));
  } catch (error) {
    const err = toError(error);
    yield put(deliveryActions.requestFailed());
    toast.error(err.message);
  }
}

function* handleGetUnallocatedPickups(action: PayloadAction<{ limit: number; offset: number; from?: string; to?: string; search?: string; warehouseIds?: number[] }>) {
  try {
    const { data } = yield call(orderApi.getUnallocatedPickups, action.payload);
    yield put(deliveryActions.getUnallocatedPickupsSuccess(data));
  } catch (error) {
    yield put(deliveryActions.getUnallocatedPickupsSuccess([]));
    toast.error(toError(error).message);
  }
}

function* handleGetUnallocatedPickupsToAppend(action: PayloadAction<{ limit: number; offset: number; from?: string; to?: string; search?: string; warehouseIds?: number[] }>) {
  try {
    const { data } = yield call(orderApi.getUnallocatedPickups, action.payload);
    yield put(deliveryActions.getUnallocatedPickupsToAppendSuccess(data));
  } catch (error) {
    yield put(deliveryActions.getUnallocatedPickupsToAppendSuccess([]));
    toast.error(toError(error).message);
  }
}

function* handleGetUnallocatedDeliveries(action: PayloadAction<{ limit: number; offset: number; from?: string; to?: string; search?: string; warehouseIds?: number[] }>) {
  try {
    const { data } = yield call(orderApi.getUnallocatedDeliveries, action.payload);
    yield put(deliveryActions.getUnallocatedDeliveriesSuccess(data));
  } catch (error) {
    yield put(deliveryActions.getUnallocatedDeliveriesSuccess([]));
    toast.error(toError(error).message);
  }
}

function* handleGetUnallocatedDeliveriesToAppend(action: PayloadAction<{ limit: number; offset: number; from?: string; to?: string; search?: string; warehouseIds?: number[] }>) {
  try {
    const { data } = yield call(orderApi.getUnallocatedDeliveries, action.payload);
    yield put(deliveryActions.getUnallocatedDeliveriesToAppendSuccess(data));
  } catch (error) {
    yield put(deliveryActions.getUnallocatedDeliveriesToAppendSuccess([]));
    toast.error(toError(error).message);
  }
}

function* handleOpenNewRoute(action: PayloadAction<{ params: RoutesQueryParams; payload: { routeConfig: IRouterConfiguration } }>) {
  const { params, payload } = action.payload;
  try {
    yield call(routeApi.createRoute, payload);
    const { data } = yield call(routeApi.getRoutes, params);
    yield put(deliveryActions.openRouteSuccess(data));
    toast.success("Opened New Route");
  } catch (error) {
    const err = toError(error);
    yield put(deliveryActions.requestFailed());
    toast.error(err.message);
  }
}

function* handleDeleteRoute(action: PayloadAction<{ id: number; params: RoutesQueryParams }>) {
  const { id, params } = action.payload;
  try {
    yield call(routeApi.deleteRoute, id);
    const { data } = yield call(routeApi.getRoutes, params);
    yield put(deliveryActions.deleteRouteSuccess(data));
    toast.success("Route Deleted");
  } catch (error) {
    const err = toError(error);
    yield put(deliveryActions.requestFailed());
    toast.error(err.message);
  }
}

export default function* watchDeliverySaga() {
  yield takeEvery(deliveryActions.getRoutes.type, handleGetRoutes);
  yield takeLatest(deliveryActions.getRoute.type, handleGetRouteDetail);
  yield takeLatest(deliveryActions.refreshRoute.type, handleRefreshRouteDetail);
  yield takeEvery(deliveryActions.getDeliveryAttempts.type, handleGetNeedActionDelivery);
  yield takeEvery(deliveryActions.getOrders.type, handleGetOrders);
  yield takeEvery(deliveryActions.getOrdersToAppend.type, handleGetOrdersToPrepend);
  yield takeLatest(deliveryActions.openRoute.type, handleOpenNewRoute);
  yield takeLatest(deliveryActions.deleteRoute.type, handleDeleteRoute);
  yield takeLatest(deliveryActions.disconnectRoute.type, handleDisconnectRoute);
  yield takeLatest(deliveryActions.recalculateRoute.type, handleRecalculateRoute);
  yield takeLatest(deliveryActions.getUnallocatedPickups.type, handleGetUnallocatedPickups);
  yield takeEvery(deliveryActions.getUnallocatedPickupsToAppend.type, handleGetUnallocatedPickupsToAppend);
  yield takeLatest(deliveryActions.getUnallocatedDeliveries.type, handleGetUnallocatedDeliveries);
  yield takeEvery(deliveryActions.getUnallocatedDeliveriesToAppend.type, handleGetUnallocatedDeliveriesToAppend);
}
